import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Send, ArrowLeft, CheckCheck, User as UserIcon, Phone, MessageCircle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 4000;

const timeStamp = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const dateLabel = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-CA", { day: "2-digit", month: "long", year: "numeric" });
};

export default function ReplyPage() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const feedRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chat/reply/${token}`);
      setSession(data.session);
      setMessages(data.messages || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Lien invalide ou expiré.");
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    const temp = { id: `tmp-${Date.now()}`, author: "admin", text, created_at: new Date().toISOString(), pending: true };
    setMessages((m) => [...m, temp]);
    setDraft("");
    try {
      const { data } = await axios.post(`${API}/chat/reply/${token}`, { text });
      setMessages((m) => m.map((x) => (x.id === temp.id ? data : x)));
    } catch (e) {
      setMessages((m) => m.map((x) => (x.id === temp.id ? { ...x, error: true, pending: false } : x)));
      setError(e?.response?.data?.detail || "Envoi impossible.");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center">
        <div className="font-body text-sm uppercase tracking-widest text-[#475569] animate-pulse">Chargement…</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white border border-[#E2E8F0] rounded-md p-8 text-center shadow-lg">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-100 text-red-600 flex items-center justify-center">
            <MessageCircle size={22} />
          </div>
          <div className="mt-5 font-display text-2xl uppercase text-[#0F172A]">Lien invalide</div>
          <p className="mt-3 font-body text-sm text-[#475569] leading-relaxed">{error}</p>
          <a href="/" className="mt-6 inline-flex items-center gap-2 font-body text-xs font-bold uppercase tracking-[0.2em] text-[#E87B3A] hover:text-[#F08A47]">
            <ArrowLeft size={14} /> Retour au site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1EA] flex flex-col" data-testid="reply-page">
      {/* HEADER */}
      <header className="bg-gradient-to-br from-[#128C7E] to-[#25D366] text-white shadow-md">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <a href="/" className="p-1.5 rounded-full hover:bg-white/15 transition-colors shrink-0" data-testid="reply-back">
              <ArrowLeft size={18} />
            </a>
            <div className="w-11 h-11 rounded-full bg-white/20 border border-white/30 flex items-center justify-center shrink-0">
              <UserIcon size={20} />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-display text-lg uppercase tracking-wide truncate">{session.name}</div>
              <div className="flex items-center gap-2 text-[11px] font-body text-white/90 mt-0.5">
                <Phone size={11} /> <a href={`tel:${session.phone.replace(/\D/g,"")}`} className="hover:underline">{session.phone}</a>
                {session.email && <span className="hidden sm:inline text-white/70">· {session.email}</span>}
              </div>
            </div>
          </div>
          <button
            onClick={load}
            aria-label="Rafraîchir"
            data-testid="reply-refresh"
            className="p-2 rounded-full hover:bg-white/15 transition-colors"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </header>

      {/* SUB HEADER — session started at */}
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-2 flex items-center justify-between text-[10px] font-body font-bold uppercase tracking-[0.24em] text-[#475569]">
          <span>Conversation Location RNC</span>
          <span>Démarrée {dateLabel(session.created_at)}</span>
        </div>
      </div>

      {/* FEED */}
      <main
        ref={feedRef}
        className="flex-1 overflow-y-auto bg-[#E7DFD3]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'><path d='M20 5v6M8 12l4 4M32 12l-4 4M5 22h6M35 22h-6M20 35v-6M8 30l4-4M32 30l-4-4' stroke='rgba(15,23,42,0.05)' stroke-width='1' fill='none'/></svg>\")",
        }}
      >
        <div className="max-w-3xl mx-auto px-3 md:px-6 py-5 space-y-2.5">
          {messages.length === 0 && (
            <div className="text-center font-body text-xs text-[#475569] uppercase tracking-widest py-8">
              Aucun message pour l'instant.
            </div>
          )}
          <AnimatePresence initial={false}>
            {messages.map((m) => {
              // for Phil (admin), his own bubbles are on the RIGHT (green), visitor's are on the LEFT (white)
              const mine = m.author === "admin";
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.22 }}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-lg shadow-[0_1px_1px_rgba(15,23,42,0.08)] ${
                      mine ? "bg-[#DCF8C6] rounded-tr-sm" : "bg-white rounded-tl-sm"
                    } ${m.error ? "opacity-70" : ""}`}
                  >
                    {!mine && (
                      <div className="font-body text-[10px] font-bold uppercase tracking-[0.2em] text-[#128C7E] mb-1">
                        {session.name}
                      </div>
                    )}
                    <div className="font-body text-[14px] text-[#0F172A] leading-relaxed whitespace-pre-wrap break-words">
                      {m.text}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 justify-end text-[10px] text-[#475569]">
                      <span>{timeStamp(m.created_at)}</span>
                      {mine && (
                        m.error ? <span className="text-red-500">!</span> :
                        m.pending ? <span className="opacity-60">·</span> :
                        <CheckCheck size={12} className="text-[#128C7E]" />
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </main>

      {/* INPUT DOCK */}
      <footer className="border-t border-[#E2E8F0] bg-[#F5F1EA] shrink-0" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="max-w-3xl mx-auto px-3 md:px-6 py-3">
          {error && (
            <div className="mb-2 text-red-600 text-xs font-body text-center">{error}</div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={`Répondre à ${session.name}…`}
              rows={1}
              data-testid="reply-input"
              className="flex-1 max-h-32 resize-none bg-white border border-[#E2E8F0] rounded-2xl px-4 py-2.5 font-body text-[14.5px] text-[#0F172A] focus:outline-none focus:border-[#128C7E] focus:ring-2 focus:ring-[#128C7E]/10"
            />
            <button
              onClick={send}
              disabled={!draft.trim() || sending}
              aria-label="Envoyer"
              data-testid="reply-send"
              className="w-11 h-11 shrink-0 rounded-full bg-[#25D366] hover:bg-[#1EBE5B] disabled:opacity-50 text-white flex items-center justify-center transition-colors"
            >
              <Send size={17} />
            </button>
          </div>
          <div className="mt-2 text-[9px] font-body uppercase tracking-widest text-[#94A3B8] text-center">
            Vos réponses apparaissent instantanément chez {session.name}.
          </div>
        </div>
      </footer>
    </div>
  );
}
